import { motion } from 'framer-motion';

// Editorial brand directory — replaces the literal constellation.
// Authentic logo or typographic wordmark tiles in a responsive grid,
// grouped by organisation where the market has groups. Markets with
// one or two brands get a larger, balanced presentation.

function Tile({ brand, isMobile, state, onActivate, onHover, large, reducedMotion }) {
  const active = state === 'active';
  const hovered = state === 'hover';
  return (
    <motion.button
      initial={reducedMotion ? { opacity: 1 } : { opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      onClick={() => onActivate(brand.id)}
      onMouseEnter={() => onHover(brand.id)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(brand.id)}
      onBlur={() => onHover(null)}
      className={`group relative flex flex-col items-center justify-center gap-3 border bg-void/40 transition-all duration-300 ${
        large ? 'px-8 py-14' : 'px-4 py-8 sm:py-10'
      } ${
        active
          ? 'border-tangerine/70 shadow-[0_0_28px_rgba(255,113,56,0.12)]'
          : hovered
          ? 'border-magenta/50'
          : 'border-white/[0.08] hover:border-white/[0.2]'
      }`}
      data-testid={`brand-tile-${brand.id}`}
      aria-label={`${brand.name} — ${brand.role || 'brand'}`}
    >
      {active && (
        <motion.span
          layoutId="tile-active"
          className="absolute inset-0 border border-tangerine/70 pointer-events-none"
        />
      )}
      {brand.logo ? (
        <img
          src={brand.logo}
          alt={brand.name}
          loading="lazy"
          className={`object-contain transition-all duration-300 ${
            large ? 'h-16 w-auto max-w-[70%]' : 'h-9 sm:h-10 w-auto max-w-[72%]'
          } ${state === 'idle' ? 'opacity-75 group-hover:opacity-100' : 'opacity-100'}`}
        />
      ) : (
        <span
          className={`font-syne font-bold uppercase tracking-wide text-center leading-tight transition-colors duration-300 ${
            large ? 'text-3xl' : 'text-base sm:text-lg'
          } ${
            active
              ? 'text-tangerine'
              : hovered
              ? 'text-violettext'
              : 'text-slate-300/90'
          }`}
        >
          {brand.name}
        </span>
      )}
      <span
        className={`font-mono text-[9px] uppercase tracking-[0.25em] transition-colors duration-300 ${
          active ? 'text-tangerine' : 'text-slate-600'
        }`}
      >
        {brand.country}
      </span>
      {active && (
        <span className="absolute top-2.5 right-2.5 w-1.5 h-1.5 rounded-full accent-gradient" />
      )}
    </motion.button>
  );
}

export default function BrandDirectory({ cluster, isMobile, activeBrandId, onActivate, hoverId, onHover, reducedMotion }) {
  const single = cluster.brands.length === 1;
  const few = cluster.brands.length <= 4;

  const groups = cluster.groupLabels
    ? Object.entries(cluster.groupLabels)
        .map(([gid, label]) => ({ gid, label, brands: cluster.brands.filter((b) => b.group === gid) }))
        .filter((g) => g.brands.length)
    : [{ gid: null, label: null, brands: cluster.brands }];

  const gridClass = (count) =>
    isMobile
      ? count === 1
        ? 'grid-cols-1'
        : 'grid-cols-2 gap-3'
      : single || count <= 2
      ? 'grid-cols-2 gap-5'
      : 'grid-cols-2 lg:grid-cols-4 gap-4';

  return (
    <div className="p-5 pt-16 sm:p-8 sm:pt-16" data-testid={`brand-directory-${cluster.id}`}>
      {groups.map((g) => (
        <div key={g.gid || 'all'} className={g.label ? 'mb-9 last:mb-0' : ''}>
          {g.label && (
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ultraviolet mb-4">
              {g.label}
            </p>
          )}
          <div className={`grid ${gridClass(g.brands.length)}`}>
            {g.brands.map((b, i) => (
              <Tile
                key={b.id}
                brand={b}
                isMobile={isMobile}
                large={(single || few) && !isMobile}
                state={activeBrandId === b.id ? 'active' : hoverId === b.id ? 'hover' : 'idle'}
                onActivate={onActivate}
                onHover={onHover}
                reducedMotion={reducedMotion}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
